import {
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { PB, SUPERVISION_ORGAN, every, propertySection, rightToDisposeSection } from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E12. Samowola budowlana — pisma petenta w postępowaniu naprawczym PINB (postępowanie wszczyna organ z urzędu): wniosek
 * o legalizację (art. 48a), dokumenty legalizacyjne i dowód opłaty legalizacyjnej (art. 48b ust. 2–3, art. 49g ust. 2,
 * art. 49 i 49d) oraz żądanie wszczęcia uproszczonego postępowania legalizacyjnego dla obiektów zakończonych co najmniej
 * 20 lat temu (art. 49f, art. 53a ust. 2–3) — ustawa Prawo budowlane (t.j. Dz. U. 2025 poz. 418, tekst
 * https://api.sejm.gov.pl/eli/acts/DU/2025/418/text.pdf — pobrany 2026-10-22). Wzór ogólnopolski tylko dla PB-15
 * (https://www.gov.pl/attachment/adf7ae2c-b7b6-44cd-aa5b-99d751436301 — pobrany 2026-10-22, DOCX: właściciel/zarządca,
 * pełnomocnik, data zakończenia budowy, opis obiektu, nieruchomość); wniosek z art. 48a i dokumenty z art. 48b to pisma
 * bez wzoru — układ według „Uwag dla EBI” (numer sprawy, termin 30 dni, rodzaj przedkładanych dokumentów). Oświadczenie
 * o prawie do dysponowania nieruchomością (dokument legalizacyjny) składane w formularzu według wzoru PB-5.
 */
const MOTION: EbiCondition = { field: "rodzajPisma", equals: "legalizacja" };
const DOCUMENTS: EbiCondition = { field: "rodzajPisma", equals: "dokumenty" };
const SIMPLIFIED: EbiCondition = { field: "rodzajPisma", equals: "pb15" };
const IN_CASE: EbiCondition = { field: "rodzajPisma", in: ["legalizacja", "dokumenty"] };
const stage = (...values: string[]): EbiCondition => every(DOCUMENTS, { field: "etap", in: values });
const LEGALISATION_DOCUMENTS = stage("pozwolenie", "pozostale", "uproszczone");

const E12: IEbiFormDefinition = {
  code: "E12",
  version: 1,
  title: "Legalizacja samowoli budowlanej",
  category: EEbiFormCategory.Construction,
  office: EEbiFormOffice.CountyUnit,
  description:
    "Wniosek o legalizację po postanowieniu o wstrzymaniu budowy, dokumenty legalizacyjne i dowód opłaty albo wniosek o uproszczoną legalizację (PB-15) obiektu sprzed co najmniej 20 lat.",
  keywords: ["samowola budowlana", "legalizacja", "PB-15", "uproszczona legalizacja", "PINB", "nadzór budowlany", "wstrzymanie budowy"],
  organ: SUPERVISION_ORGAN,
  legalBasis: `art. 48a, art. 48b, art. 49, art. 49d, art. 49f, art. 49g i art. 53a ust. 2 i 3 ${PB}`,
  request: "W sprawie samowoli budowlanej opisanej poniżej składam wniosek albo przedkładam dokumenty.",
  feeLabel: "wg stawek",
  fee: "Wniosek o legalizację i wniosek PB-15 — bez opłaty skarbowej. Opłata legalizacyjna (art. 49d) ustalana postanowieniem: przy budowie wymagającej pozwolenia (lub z art. 29 ust. 1 pkt 1–3) — według zasad kary z art. 59f ze stawką podwyższoną 50-krotnie, przy pozostałych budowach — 5000 zł albo 2500 zł; uproszczona legalizacja — bez opłaty legalizacyjnej. Pełnomocnictwo — 17 zł.",
  deadlineLabel: "30 dni na wniosek",
  deadline:
    "Wniosek o legalizację — w terminie 30 dni od dnia doręczenia postanowienia o wstrzymaniu budowy (art. 48a); dokumenty legalizacyjne — w terminie wyznaczonym postanowieniem, nie krótszym niż 60 dni (art. 48b ust. 1, art. 49g ust. 1).",
  variantField: "rodzajPisma",
  variants: {
    legalizacja: { title: "Wniosek o legalizację (art. 48a Prawa budowlanego)", documentType: "Wniosek" },
    dokumenty: { title: "Przedłożenie dokumentów legalizacyjnych", documentType: "Pismo" },
    pb15: { title: "Wniosek o wszczęcie uproszczonego postępowania legalizacyjnego (PB-15)", documentType: "Wniosek" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj pisma",
      fields: [
        feeInfo(
          "infoPostepowanie",
          "Postępowanie w sprawie samowoli budowlanej wszczyna organ nadzoru budowlanego z urzędu. Przez Elektroniczne Biuro Interesanta możesz złożyć wniosek o legalizację w odpowiedzi na postanowienie o wstrzymaniu budowy, przedłożyć dokumenty legalizacyjne albo — jako właściciel lub zarządca obiektu, którego budowę zakończono co najmniej 20 lat temu — wystąpić z żądaniem wszczęcia uproszczonego postępowania legalizacyjnego.",
        ),
        {
          code: "rodzajPisma",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            { value: "legalizacja", label: "wniosek o legalizację (art. 48a) — po doręczeniu postanowienia o wstrzymaniu budowy" },
            { value: "dokumenty", label: "dokumenty legalizacyjne albo dowód uiszczenia opłaty legalizacyjnej — na postanowienie organu" },
            { value: "pb15", label: "wniosek o wszczęcie uproszczonego postępowania legalizacyjnego (PB-15)" },
          ],
        },
      ],
    },
    applicantSection(),
    representationSection(),
    {
      code: "sprawa",
      title: "Sprawa w organie nadzoru budowlanego",
      visibleWhen: IN_CASE,
      fields: [
        { code: "znakSprawy", type: "text", label: "Znak sprawy", required: true, width: "half", maxLength: 100 },
        { code: "dataPostanowienia", type: "date", label: "Data doręczenia postanowienia, na które odpowiadasz", required: true, width: "half" },
        {
          ...feeInfo(
            "infoLegalizacja",
            "Wniosek o legalizację składa się w terminie 30 dni od dnia doręczenia postanowienia o wstrzymaniu budowy (po zażaleniu — od dnia, w którym postanowienie stało się ostateczne). Organ nałoży następnie obowiązek przedłożenia dokumentów legalizacyjnych w terminie nie krótszym niż 60 dni.",
          ),
          visibleWhen: MOTION,
        },
      ],
    },
    {
      code: "dokumenty",
      title: "Przedkładane dokumenty",
      visibleWhen: DOCUMENTS,
      fields: [
        {
          code: "etap",
          type: "radio",
          label: "Przedkładam",
          required: true,
          options: [
            {
              value: "pozwolenie",
              label: "dokumenty legalizacyjne dla budowy wymagającej pozwolenia na budowę albo budowy z art. 29 ust. 1 pkt 1–3 (art. 48b ust. 2)",
            },
            { value: "pozostale", label: "dokumenty legalizacyjne dla pozostałych budów (art. 48b ust. 3)" },
            { value: "uproszczone", label: "dokumenty w uproszczonym postępowaniu legalizacyjnym (art. 49g ust. 2)" },
            { value: "oplata", label: "dowód uiszczenia opłaty legalizacyjnej (po postanowieniu o jej ustaleniu)" },
          ],
        },
      ],
    },
    {
      code: "obiekt",
      title: "Dane dotyczące obiektu",
      visibleWhen: { field: "rodzajPisma", filled: true },
      fields: [
        {
          code: "dataZakonczenia",
          type: "text",
          label: "Data zakończenia budowy",
          required: true,
          visibleWhen: SIMPLIFIED,
          width: "half",
          maxLength: 50,
          placeholder: "np. 1998 r.",
          hint: "Uproszczone postępowanie dotyczy obiektów, których budowę zakończono co najmniej 20 lat temu (art. 49f ust. 1).",
        },
        {
          code: "obiektOpis",
          type: "textarea",
          label: "Opis obiektu (robót budowlanych)",
          requiredWhen: { any: [MOTION, SIMPLIFIED] },
          maxLength: 3000,
        },
      ],
    },
    propertySection("Dane nieruchomości, na której znajduje się obiekt", { any: [MOTION, SIMPLIFIED, LEGALISATION_DOCUMENTS] }),
    rightToDisposeSection(LEGALISATION_DOCUMENTS),
    replySection(),
  ],
  attachments: [
    {
      code: "zaswiadczenieGminy",
      label:
        "Zaświadczenie wójta, burmistrza albo prezydenta miasta o zgodności budowy z ustaleniami planu miejscowego i innymi aktami prawa miejscowego albo decyzji o warunkach zabudowy",
      visibleWhen: stage("pozwolenie", "pozostale"),
      required: true,
    },
    {
      code: "projektBudowlany",
      label: "Projekt zagospodarowania działki lub terenu oraz projekt architektoniczno-budowlany",
      hint: "Wraz z opiniami, uzgodnieniami i pozwoleniami wymaganymi przepisami odrębnych ustaw (art. 48b ust. 2 pkt 2 w zw. z art. 33 ust. 2 pkt 1) — pliki podpisane przez projektanta.",
      multiple: true,
      visibleWhen: stage("pozwolenie"),
      required: true,
    },
    {
      code: "projektTechniczny",
      label: "Projekt techniczny",
      hint: "Ustawa wymaga dwóch egzemplarzy projektu technicznego (art. 48b ust. 2 pkt 2).",
      visibleWhen: stage("pozwolenie"),
      required: true,
    },
    {
      code: "decyzjeOdrebne",
      label: "Decyzja o środowiskowych uwarunkowaniach i inne dokumenty wymagane przepisami odrębnych ustaw",
      hint: "Jeśli są wymagane — także załącznik graficzny z art. 48b ust. 4.",
      multiple: true,
      visibleWhen: stage("pozwolenie"),
    },
    {
      code: "projektZagospodarowania",
      label: "Projekt zagospodarowania działki lub terenu",
      visibleWhen: stage("pozostale"),
      required: true,
    },
    {
      code: "inwentaryzacja",
      label: "Geodezyjna inwentaryzacja powykonawcza obiektu budowlanego",
      visibleWhen: stage("uproszczone"),
      required: true,
    },
    {
      code: "ekspertyza",
      label: "Ekspertyza techniczna",
      hint: "Sporządzona przez osobę z odpowiednimi uprawnieniami budowlanymi — czy stan techniczny obiektu nie stwarza zagrożenia dla życia lub zdrowia ludzi i pozwala na bezpieczne użytkowanie (art. 49g ust. 2 pkt 3).",
      visibleWhen: stage("uproszczone"),
      required: true,
    },
    {
      code: "oplataLegalizacyjna",
      label: "Dowód uiszczenia opłaty legalizacyjnej",
      visibleWhen: stage("oplata"),
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default E12;
